import api from 'services/api';
import { REJECT, array, nullable, num, object, unknownRecord, unknownValue, type Received } from 'validation';

import { readResponse } from 'api/shared/readResponse';

// PUT /workflowActions/:workflow_action_id — saves the operator's inputs on an action. Source of truth: nap-backend
// `routes/workflows.js`, which answers with the updated `workflow_actions` row plus its fresh `dropdown_parameters`.
// This contract declares what the inbox keeps of that receipt: which action of which workflow, and whether it is still
// open. The detail panel re-reads the workflow after a save instead of reading the rest.

/** The request body: the inbox only ever writes these two. */
export interface SaveWorkflowAction {
  readonly response_inputs: readonly { readonly label: string; readonly value: string }[];
  readonly dropdown_parameters?: readonly { readonly parameter_id: number; readonly value: string }[];
}

export interface UpdatedWorkflowAction {
  readonly workflow_action_id: number;
  readonly workflow_id: number;
  readonly response_options: readonly unknown[];
  readonly response: Readonly<Record<string, unknown>> | null;
}

const updatedSchema = object<UpdatedWorkflowAction>({
  workflow_action_id: num(REJECT),
  workflow_id: num(REJECT),
  response_options: array(unknownValue()),
  response: nullable(unknownRecord()),
});

/**
 * PUT `/workflowActions/:id`. Rejects on HTTP error: the save did not happen. Answers with the unreadable arm when the
 * save did happen and its receipt cannot be read, which is not a failed save (#1035).
 */
export const putWorkflowAction = async (workflowActionId: number, body: SaveWorkflowAction): Promise<Received<UpdatedWorkflowAction>> => {
  const res = await api.put(`/workflowActions/${workflowActionId}`, body);
  return readResponse(res.data, { schema: updatedSchema, source: 'putWorkflowAction' });
};
